import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, Calendar, User, Send, Eye } from 'lucide-react';

const ProviderReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responseText, setResponseText] = useState({});
  const [submittingResponse, setSubmittingResponse] = useState({});

  useEffect(() => {
    fetchProviderReviews();
  }, []);

  const fetchProviderReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/reviews`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      } else {
        setError('Failed to load reviews');
      }
    } catch (err) {
      setError('Network error loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseSubmit = async (reviewId) => {
    const response = responseText[reviewId]?.trim();
    if (!response) return;

    setSubmittingResponse(prev => ({ ...prev, [reviewId]: true }));

    try {
      const token = localStorage.getItem('token');
      const apiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/reviews/${reviewId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response: response
        })
      });

      if (apiResponse.ok) {
        // Update the local state
        setReviews(prev => prev.map(review => 
          review.id === reviewId 
            ? { 
                ...review, 
                providerResponse: response, 
                providerResponseAt: new Date().toISOString() 
              }
            : review
        ));
        
        // Clear the response text
        setResponseText(prev => ({ ...prev, [reviewId]: '' }));
      } else {
        const errorData = await apiResponse.json();
        alert(errorData.detail || 'Failed to submit response');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setSubmittingResponse(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading your reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Your Reviews</h2>
        <p className="text-sm text-gray-600">{reviews.length} total reviews</p>
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <MessageCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Reviews Yet</h3>
          <p>When customers leave reviews for your services, they will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                    <User className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(review.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {renderStars(review.rating)}
                  <div className="mt-2">
                    {getStatusBadge(review.status)}
                  </div>
                </div>
              </div>

              {/* Service Info */}
              {review.serviceTitle && (
                <div className="mb-3 text-sm">
                  <span className="font-medium text-gray-600">Service: </span>
                  <span className="text-gray-900">{review.serviceTitle}</span>
                </div>
              )}

              {/* Review Comment */}
              <div className="mb-4">
                <p className="text-gray-700 leading-relaxed">{review.comment}</p>
              </div>

              {/* Review Status Info */}
              {review.status === 'pending' && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 text-yellow-600 mr-2" />
                    <span className="text-sm text-yellow-700">
                      This review is pending admin approval before it becomes visible to customers.
                    </span>
                  </div>
                </div>
              )}

              {review.status === 'rejected' && review.adminComment && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <span className="text-sm text-red-700">
                    <strong>Admin note:</strong> {review.adminComment}
                  </span>
                </div>
              )}

              {/* Existing Provider Response */}
              {review.providerResponse && (
                <div className="mb-4 p-4 bg-gray-50 border-l-4 border-blue-200 rounded">
                  <div className="flex items-center mb-2">
                    <MessageCircle className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-800">Your Response</span>
                    <span className="text-xs text-blue-600 ml-2">
                      {formatDate(review.providerResponseAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{review.providerResponse}</p>
                </div>
              )}

              {/* Response Form (only for approved reviews without responses) */}
              {review.status === 'approved' && !review.providerResponse && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Respond to this review
                  </label>
                  <div className="space-y-3">
                    <textarea
                      value={responseText[review.id] || ''}
                      onChange={(e) => setResponseText(prev => ({ 
                        ...prev, 
                        [review.id]: e.target.value 
                      }))}
                      rows={3}
                      maxLength={500}
                      placeholder="Thank the customer and address any concerns professionally..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {(responseText[review.id] || '').length}/500 characters
                      </span>
                      <button
                        onClick={() => handleResponseSubmit(review.id)}
                        disabled={
                          !responseText[review.id]?.trim() || 
                          submittingResponse[review.id]
                        }
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {submittingResponse[review.id] ? 'Sending...' : 'Send Response'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderReviews;